import React, { useEffect, useState } from 'react';
import { Preferences as Storage } from '@capacitor/preferences';
import { Center, Heading, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { CapacitorWifiConnect } from '@falconeta/capacitor-wifi-connect';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { IS_ANDROID, IS_NATIVE } from 'clipsal-cortex-utils/src/constants/common-constants';
import { useOnMount } from 'clipsal-cortex-utils/src/hooks/use-on-mount';

import { useReduxDispatch } from '../../../app/store';
import { ArcButton } from '../../../common/components/ArcButton';
import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { WifiNetwork, WifiNetworkList } from '../../../common/components/WifiNetworkList';
import { WiFiPasswordDialog } from '../../../common/components/WiFiPasswordDialog';
import { timeout } from '../../../utils/common/common-utils';
import { EVSE_FACTORY_PIN_CODE } from '../constants';
import { enrolEvCharger, resetEnrollState, selectDeviceSetupState } from '../device-setup/deviceSetupSlice';
import { useDebugLogs } from '../use-debug-logs';
import { connectToSelectedNetwork, fetchWifiNetworks, getAuthToken, putWifiSettings } from './wifi-connect-helper';
import { addBearerToken, selectWifiConfigState } from './wifiConfigSlice';

type SetupStatus = 'READY' | 'SCANNING' | 'CONNECTING';

type State = {
  selectedWifiNetworkIndex: number | null;
  selectedWifiNetworkPassword: string;
  status: SetupStatus;
  wifiNetworks: WifiNetwork[];
};

const INITIAL_STATE: State = {
  selectedWifiNetworkIndex: null,
  selectedWifiNetworkPassword: '',
  status: 'READY',
  wifiNetworks: [],
};

/* istanbul ignore next -- @preserve */
export const DeviceWifiConnection = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { debugToast } = useDebugLogs();
  const { onOpen, onClose, isOpen } = useDisclosure();
  const { bearerToken, isEditing } = useSelector(selectWifiConfigState);
  const { hasInitiatedEnroll, qrCodeValues } = useSelector(selectDeviceSetupState);
  const dispatch = useReduxDispatch();
  const [state, setState] = useState(INITIAL_STATE);
  const { selectedWifiNetworkIndex, status, wifiNetworks } = state;

  useEffect(() => {
    if (hasInitiatedEnroll) {
      navigate('../device-join');
    }

    if (!qrCodeValues) {
      navigate('../device-scan');
    }

    return () => {
      dispatch(resetEnrollState());
    };
  }, [dispatch, hasInitiatedEnroll, qrCodeValues, navigate]);

  async function fetchLocalWifiNetworks() {
    let token = null;
    try {
      setState((prevState) => ({
        ...prevState,
        selectedWifiNetworkIndex: null,
        selectedWifiNetworkPassword: '',
        status: 'SCANNING',
        wifiNetworks: [],
      }));

      token = await getAuthToken(EVSE_FACTORY_PIN_CODE, toast);
      dispatch(addBearerToken(token));
    } catch (error) {
      debugToast('Failed to get auth token', JSON.stringify(error), 8000);
      setState((prevState) => ({
        ...prevState,
        status: 'READY',
      }));
      if (IS_NATIVE) {
        // Disconnect from the device's wifi network.
        await CapacitorWifiConnect.disconnect();
      }

      Sentry.captureException(error, {
        tags: {
          category: 'feature',
          feature: 'ev-charger',
          component: 'DeviceWifiConnection',
          method: 'getAuthToken',
        },
      });
      toast({
        title: t('EVSE.error accessing charger'),
        description: `${t('EVSE.restart charger and try again')} ${t('Common.if this persists contact support')}`,
        status: 'error',
        isClosable: true,
      });
      return;
    }

    try {
      debugToast('Fetching wifi networks', 'Requesting wifi networks from EVSE Charger');
      const wifiNetworks = (await fetchWifiNetworks(token!, toast)) as WifiNetwork[];
      debugToast('Fetched wifi networks', `Found ${wifiNetworks.length} networks`);

      setState((prevState) => ({
        ...prevState,
        status: 'READY',
        wifiNetworks,
      }));
    } catch (error) {
      debugToast('Error fetching wifi networks', JSON.stringify(error), 8000);
      setState((prevState) => ({
        ...prevState,
        status: 'READY',
      }));
      if (IS_NATIVE) {
        // Disconnect from the device's wifi network.
        await CapacitorWifiConnect.disconnect();
      }

      Sentry.captureException(error, {
        tags: {
          category: 'feature',
          feature: 'ev-charger',
          component: 'DeviceWifiConnection',
          method: 'fetchWifiNetworks',
        },
      });
      toast({
        title: t('Set Up Hardware.error fetching wifi'),
        description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
        status: 'error',
        isClosable: true,
      });
    }
  }

  useOnMount(fetchLocalWifiNetworks);

  function handleWifiNetworkClick(index: number | null) {
    // Clicking the same network again unselects it.
    const newIndex = index === selectedWifiNetworkIndex ? null : index;
    setState((prevState) => ({ ...prevState, selectedWifiNetworkIndex: newIndex }));
  }

  async function handleConnectToWifiNetwork(password: string) {
    try {
      let currentSsid = '';
      if (IS_NATIVE) {
        const current = await CapacitorWifiConnect.getDeviceSSID();
        currentSsid = current?.value;
        const evseSsid = qrCodeValues?.wifi?.ssid;

        if (currentSsid !== evseSsid) {
          toast({
            title: t('EVSE.wifi connection'),
            description: t('EVSE.device is not connected', { currentSsid, evseSsid }),
            status: 'error',
            isClosable: true,
          });
          throw new Error(
            `Device is not connected to expected EVSE Wi-Fi network. Connected to ${currentSsid}, expected ${evseSsid}`
          );
        }
      }
      if (selectedWifiNetworkIndex === null) {
        throw new Error('No network selected! Unable to connect device to wifi network if none is selected.');
      }
      setState((prevState) => ({
        ...prevState,
        status: 'CONNECTING',
      }));

      debugToast(
        'Putting wifi settings',
        `SSID: ${wifiNetworks[selectedWifiNetworkIndex].ssid}, Password: ${password}`
      );
      try {
        await putWifiSettings(
          { ssid: wifiNetworks[selectedWifiNetworkIndex].ssid, password },
          bearerToken ?? '',
          toast
        );
      } catch (error) {
        debugToast('Error connecting EVSE to wifi network', JSON.stringify(error), 8000);

        // Only throw if it's NOT a timeout error
        const isTimeout = error instanceof Error && error.message.toLowerCase().includes('timeout');
        if (!isTimeout) {
          throw error;
        }
      }

      // Clear stored EVSE certs after configuring Wi-Fi settings.
      await Storage.remove({ key: 'evseCerts' });

      if (IS_NATIVE) {
        try {
          if (IS_ANDROID) {
            await CapacitorWifiConnect.disconnect();
            await timeout(10_000);
          } else {
            await timeout(10_000); // Wait for the EVSE to apply Wi-Fi settings and reboot before trying to connect to the new network.

            await connectToSelectedNetwork(currentSsid, wifiNetworks[selectedWifiNetworkIndex].ssid, t, password);
          }
          toast({
            title: t('EVSE.succesfully connected'),
            description: `SSID: ${wifiNetworks[selectedWifiNetworkIndex].ssid}`,
            status: 'success',
            isClosable: true,
            duration: 8000,
          });
        } catch (error) {
          setState((prevState) => ({
            ...prevState,
            selectedWifiNetworkIndex: null,
            selectedWifiNetworkPassword: '',
            status: 'READY',
          }));
          toast({
            title: t('Offline.title'),
            description: `${(error as Error).message}`,
            status: 'error',
            isClosable: true,
            duration: 15000,
          });
          return;
        }
      }
    } catch (error) {
      debugToast('Error connecting to wifi network', JSON.stringify(error), 8000);

      Sentry.captureException(error, {
        tags: {
          category: 'feature',
          feature: 'ev-charger',
          component: 'DeviceWifiConnection',
          method: 'handleConnectToWifiNetwork',
        },
      });
      navigate('../wifi-setup-fail');
      return;
    }

    if (isEditing) {
      navigate('../device-wifi-configuration');
    } else {
      dispatch(enrolEvCharger(qrCodeValues!));
    }

    setState((prevState) => ({
      ...prevState,
      status: 'READY',
    }));
  }

  return (
    <SlidingAnimationPageBase title={t('Common.device setup')} backURL="../device-scan">
      <Center flexDirection="column" px={5} mt={4} textAlign="center">
        <Heading>{t('Set Up Hardware.select your wifi')}</Heading>
        <Text align="left" mb={4} mt={1}>
          {t('EVSE.connect charger to wifi')}
        </Text>

        <WifiNetworkList
          wifiNetworks={wifiNetworks}
          selectedIndex={selectedWifiNetworkIndex}
          onClick={handleWifiNetworkClick}
          isLoading={status === 'SCANNING'}
        />

        <ArcButton
          data-testid="retry-button"
          hidden={selectedWifiNetworkIndex !== null}
          onClick={fetchLocalWifiNetworks}
          isDisabled={status !== 'READY'}
          mt={8}
          w={'80%'}
          arcColor="#3DCD57"
        >
          {t('Set Up Hardware.scan networks')}
        </ArcButton>

        <ArcButton
          data-testid="ev-charger-continue"
          hidden={selectedWifiNetworkIndex === null}
          onClick={onOpen}
          mt={8}
          w={'80%'}
          arcColor="#3DCD57"
          isDisabled={status !== 'READY'}
          isLoading={status === 'CONNECTING'}
        >
          {t('Common.continue')}
        </ArcButton>
      </Center>

      <WiFiPasswordDialog
        ssid={selectedWifiNetworkIndex !== null ? wifiNetworks[selectedWifiNetworkIndex]?.ssid : ''}
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={(password) => {
          handleConnectToWifiNetwork(password);
        }}
      />
    </SlidingAnimationPageBase>
  );
};
